import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {DecimalPipe, NgClass, NgFor, NgIf, NgTemplateOutlet, TitleCasePipe,} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import {MatButton, MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Subject, takeUntil} from 'rxjs';

import {TranslocoPipe} from "@ngneat/transloco";
import {MatBadge} from "@angular/material/badge";
import {BillingInfoDTO, CartItem, CartModel, EnumBillingType, OrderStatus, ProductCategory} from "./cart.types";
import {CartService} from "./cart.service";
import {MatError, MatFormField, MatLabel, MatPrefix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatOption} from "@angular/material/core";
import {MatSelect} from "@angular/material/select";
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {
    CompanyInvoiceDataService
} from "../../../features/admin/components/smm-invoice/billing-information/smm.companybillinginfo.service";
import {
    PersonInvoiceDataService
} from "../../../features/admin/components/smm-invoice/billing-information/smm.personbillinginfo.service";
import {
    InvoiceDataCompany,
    InvoiceDataPerson
} from "../../../features/admin/components/smm-invoice/billing.info.types";
import {SmmButtonComponent} from "../smm-button/smm.button.component";
import {RouterLink} from "@angular/router";
import {FuseAlertComponent} from "../../../../@fuse/components/alert";
import {ModuleAndResourceIDs} from "../../../features/admin/components/feat-mgmt/feat-mgmt.types";
import {MatDivider} from "@angular/material/divider";
import {FuseConfirmationConfig, FuseConfirmationService} from "../../../../@fuse/services/confirmation";

@Component({
    selector: 'cart',
    templateUrl: './cart.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'cart',
    standalone: true,
    imports: [
        MatButtonModule,
        NgIf,
        MatIconModule,
        MatTooltipModule,
        NgFor,
        NgClass,
        NgTemplateOutlet,
        TranslocoPipe,
        MatBadge,
        MatFormField,
        MatLabel,
        MatInput,
        TitleCasePipe,
        DecimalPipe,
        MatOption,
        MatSelect,
        ReactiveFormsModule,
        MatError,
        MatPrefix,
        SmmButtonComponent,
        RouterLink,
        FuseAlertComponent,
        MatDivider,
    ],
})
export class CartComponent implements OnInit, OnDestroy {
    @ViewChild('cartOrigin') private _cartOrigin: MatButton;
    @ViewChild('cartPanel')
    private _cartPanel: TemplateRef<any>;
    billingInfo: UntypedFormGroup;

    persons: InvoiceDataPerson[] = [];
    companies:InvoiceDataCompany[] = [];


    cart: CartModel;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _cartService = inject(CartService);
    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _formBuilder: UntypedFormBuilder,
        private _companyInvoiceDataService: CompanyInvoiceDataService,
        private _personInvoiceDataService: PersonInvoiceDataService,
        private readonly _fuseConfirmationService:FuseConfirmationService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to car service
        this._cartService.data$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(cartModel => {
                this.cart = cartModel;
                this._changeDetectorRef.detectChanges();
            });

        this._personInvoiceDataService.data$.pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
            this.persons = value;
            this._changeDetectorRef.detectChanges();
        })

        this._companyInvoiceDataService.data$.pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
            this.companies = value;
            this._changeDetectorRef.detectChanges();
        })

        // Create event creator form
        this.billingInfo = this._formBuilder.group({
            info          : ['', [Validators.required]],
        });
    }

    formatDescription(desc:string){
        const entries:string[] = desc.split('\n');
        return entries.map(entry => {
            const parts = entry.trim().split(':');
            let line:string =  parts.length >= 2 ? `<span class="font-semibold">${parts[0]}</span>:${parts.slice(1).join(':')}`.trim() : entry.trim();
            line=`<div>${line}</div>`
            return line;
        }).join('<div class="bg-default h-0.5 w-full my-1 -mx-2"></div>');
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._cartPanel || !this._cartOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._cartPanel, this._viewContainerRef)
        );

        this._cartService.refreshCart();
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    updateAmount(cartItem:CartItem, value:number){
        cartItem.count = value;
        this._cartService.updateCartItem(cartItem);
    }

    delete(cartItem:CartItem){
        this._cartService.deleteItem(cartItem);
    }

    getTotal(){

    }


    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    goToCheckout() {
        let billingInfo = this.billingInfo.getRawValue().info as BillingInfoDTO;
        if(this.billingInfo.get('info').value.toString().trim() === ""){
            billingInfo = null;
        }else{
            billingInfo.internalOrderId = this.cart?.id;
        }

        const dialogInfo:FuseConfirmationConfig = {
                "title": "Confirmare comandă",
                "message": `Modulele vor fi activate automat, iar resursele actualizate imediat după confirmarea plății.<br><hr>
                            Pentru ca modificările să se reflecte corect, fiecare membru al echipei <b>trebuie</b> să se delogheze și să se autentifice din nou.<br>`,

                "icon": {
                    "show": true,
                    "name": "heroicons_outline:information-circle",
                    "color": "primary"
                },
                "actions": {
                    "confirm": {
                        "show": true,
                        "label": "Ok",
                        "color": "primary"
                    }
                },
                "dismissible": true
        };


        // Open the confirmation and save the reference
        const dialogRef = this._fuseConfirmationService.open(dialogInfo);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if(result == "confirmed"){
                this._cartService.checkout(billingInfo);
            }
        });

    }

    cancelOrder(){
        this._cartService.cancel();
    }

    cartBlocked():boolean{
        return !this.cart?.orderStatus || this.cart?.orderStatus == OrderStatus.PENDING_PAYMENT;
    }

    getBillingInfo(id:string, type:EnumBillingType):BillingInfoDTO{
        return {
            billingType: type,
            personalBillingId: type == EnumBillingType.PERSONAL? id:null,
            companyBillingId: type == EnumBillingType.COMPANY? id:null,
            internalOrderId: this.cart?.id
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._cartOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    protected readonly ProductCategory = ProductCategory;
    protected readonly OrderStatus = OrderStatus;
    protected readonly EnumBillingType = EnumBillingType;
    protected readonly close = close;
    protected readonly ModuleAndResourceIDs = ModuleAndResourceIDs;
}
